import type { DeviceKind, Profile } from './contract';
import { randomBase64 } from './crypto';

const PROFILE_KEY = 'drop.profile';
const PAIRS_KEY = 'drop.pairs';
const adjectives = [
  'Swift', 'Calm', 'Bright', 'Kind', 'Quiet',
  'Lucky', 'Warm', 'Clever', 'Brave', 'Gentle',
];
const animals = [
  'Otter', 'Fox', 'Panda', 'Robin', 'Koala',
  'Dolphin', 'Badger', 'Raven', 'Lynx', 'Hare',
];
const colors = [
  '#dce9fb', '#f7e6d8', '#e6e5f4', '#e7edde', '#f3dde5', '#d7eeec',
];

function id(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return `${randomBase64(16)}-${randomBase64(8)}`.replace(/[^a-zA-Z0-9-]/g, '');
}

function stableNumber(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index++) {
    result = Math.imul(result ^ value.charCodeAt(index), 16777619);
  }
  return result >>> 0;
}

function deviceInfo(): Pick<Profile, 'kind' | 'platform'> {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/i.test(ua);
  const android = /Android/i.test(ua);
  const mobile = /Mobile/i.test(ua);
  const kind: DeviceKind =
    ios || android
      ? mobile
        ? 'phone'
        : 'tablet'
      : /MacBook|Windows NT.*Touch/i.test(ua)
        ? 'laptop'
        : 'desktop';
  const browser = /Edg(?:e|A|iOS)?\//.test(ua)
    ? 'Edge'
    : /Firefox\/|FxiOS/.test(ua)
      ? 'Firefox'
      : /CriOS|Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const platform = ios
    ? `${/iPad/.test(ua) ? 'iPad' : 'iPhone'} · ${browser}`
    : android
      ? `Android · ${browser}`
      : `${/Win/i.test(ua) ? 'Windows' : /Mac/i.test(ua) ? 'Mac' : /Linux/i.test(ua) ? 'Linux' : 'Web'} · ${browser}`;
  return { kind, platform };
}

export interface LocalIdentity {
  profile: Profile;
  secret: string;
}

export function loadIdentity(): LocalIdentity {
  let stored: Partial<LocalIdentity> | undefined;
  try {
    stored = JSON.parse(
      localStorage.getItem(PROFILE_KEY) ?? 'null',
    ) as Partial<LocalIdentity> | undefined;
  } catch {
    /* use defaults */
  }

  const deviceId = stored?.profile?.deviceId || id();
  const info = deviceInfo();
  const index = stableNumber(deviceId);
  const animalName = animals[Math.floor(index / 17) % animals.length];
  const profile: Profile = {
    deviceId,
    name:
      stored?.profile?.name ||
      `${adjectives[index % adjectives.length]} ${animalName}`,
    avatar:
      stored?.profile?.avatar ||
      animalName.toLowerCase(),
    color: (colors.includes(stored?.profile?.color || '') ? stored!.profile!.color! : colors[index % colors.length]),
    kind: info.kind,
    platform: info.platform,
  };
  const identity = { profile, secret: stored?.secret || randomBase64(32) };
  saveIdentity(identity);
  return identity;
}

export function saveIdentity(value: LocalIdentity): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(value));
  } catch {
    /* private browsing */
  }
}

export function loadPairKeys(): Record<string, string> {
  try {
    return JSON.parse(
      localStorage.getItem(PAIRS_KEY) ?? '{}',
    ) as Record<string, string>;
  } catch {
    return {};
  }
}

export function savePairKeys(keys: Record<string, string>): void {
  try {
    localStorage.setItem(PAIRS_KEY, JSON.stringify(keys));
  } catch {
    /* private browsing */
  }
}
